import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Divider,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';

/**
 * DeletePermissionDialog component
 * Confirmation dialog for deleting a permission with safety checks
 */
const DeletePermissionDialog = ({ open, permission, onClose, onSuccess, deletePermission, getPermissionUsage }) => {
  const [loading, setLoading] = useState(false);
  const [usageData, setUsageData] = useState(null);
  const [loadingUsage, setLoadingUsage] = useState(false);
  const [error, setError] = useState(null);
  const [forceDelete, setForceDelete] = useState(false);

  // Fetch usage data when dialog opens
  useEffect(() => {
    if (open && permission) {
      fetchUsageData();
      setForceDelete(false);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, permission]);

  const fetchUsageData = async () => {
    if (!permission) return;

    try {
      setLoadingUsage(true);
      setError(null);
      const data = await getPermissionUsage(permission.id);
      setUsageData(data);
    } catch (err) {
      setError(err.message || 'Failed to fetch usage data');
    } finally {
      setLoadingUsage(false);
    }
  };

  const handleDelete = async () => {
    if (!permission) return;

    // Check for children
    if (permission.is_parent || (permission.children && permission.children.length > 0)) {
      setError('Cannot delete permission with children. Please delete all child permissions first.');
      return;
    }

    // Check for usage without force
    if (usageData && usageData.usage_count > 0 && !forceDelete) {
      setError(`This permission is used in ${usageData.usage_count} role(s). Check "Force Delete" to proceed.`);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await deletePermission(permission.id, forceDelete);
      onSuccess();
    } catch (err) {
      setError(err.message || err || 'Failed to delete permission');
      setLoading(false);
    }
  };

  if (!permission) {
    return null;
  }

  const hasChildren = permission.is_parent || (permission.children && permission.children.length > 0);
  const isUsed = usageData && usageData.usage_count > 0;
  const canDelete = !hasChildren;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
        <WarningIcon />
        Delete Permission
      </DialogTitle>

      <DialogContent dividers>
        {loadingUsage ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={30} />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Permission Info */}
            <Box>
              <Typography variant="body1" gutterBottom>
                Are you sure you want to delete this permission?
              </Typography>
              <Box sx={{ mt: 2, p: 2, backgroundColor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="subtitle2" fontWeight="medium">
                  {permission.name}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                  {permission.path}
                </Typography>
              </Box>
            </Box>

            <Divider />

            {/* Safety Checks */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Safety Checks:
              </Typography>

              {/* Children Check */}
              {hasChildren ? (
                <Alert severity="error" icon={<ErrorIcon />} sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight="medium" gutterBottom>
                    Cannot delete: Permission has children
                  </Typography>
                  <Typography variant="caption">
                    This permission has {permission.children_count || permission.children.length} child permission(s).
                    You must delete all child permissions before deleting the parent.
                  </Typography>
                  {permission.children && permission.children.length > 0 && (
                    <List dense sx={{ mt: 1 }}>
                      {permission.children.slice(0, 5).map((child) => (
                        <ListItem key={child.id} sx={{ py: 0.5 }}>
                          <ListItemText
                            primary={child.name}
                            secondary={child.path}
                            primaryTypographyProps={{ variant: 'caption' }}
                            secondaryTypographyProps={{ variant: 'caption', sx: { fontFamily: 'monospace' } }}
                          />
                        </ListItem>
                      ))}
                      {permission.children.length > 5 && (
                        <Typography variant="caption" color="text.secondary">
                          ... and {permission.children.length - 5} more
                        </Typography>
                      )}
                    </List>
                  )}
                </Alert>
              ) : (
                <Alert severity="success" sx={{ mb: 2 }}>
                  ✓ No child permissions
                </Alert>
              )}

              {/* Usage Check */}
              {isUsed ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight="medium" gutterBottom>
                    Warning: Permission is in use
                  </Typography>
                  <Typography variant="caption" gutterBottom display="block">
                    This permission is currently assigned to {usageData.usage_count} role(s),
                    affecting {usageData.total_users_affected} user(s).
                  </Typography>
                  <List dense sx={{ mt: 1 }}>
                    {usageData.roles.slice(0, 3).map((role) => (
                      <ListItem key={role.role_id} sx={{ py: 0.5 }}>
                        <ListItemText
                          primary={role.role_name}
                          secondary={`${role.role_type.toUpperCase()} • ${role.user_count} users`}
                          primaryTypographyProps={{ variant: 'caption' }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                    ))}
                    {usageData.roles.length > 3 && (
                      <Typography variant="caption" color="text.secondary">
                        ... and {usageData.roles.length - 3} more roles
                      </Typography>
                    )}
                  </List>
                  {canDelete && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={forceDelete}
                          onChange={(e) => setForceDelete(e.target.checked)}
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="caption">
                          Force delete (removes permission from all roles)
                        </Typography>
                      }
                      sx={{ mt: 1 }}
                    />
                  )}
                </Alert>
              ) : (
                <Alert severity="success">
                  ✓ Not assigned to any roles
                </Alert>
              )}
            </Box>

            {/* Error Message */}
            {error && (
              <Alert severity="error">
                {error}
              </Alert>
            )}

            {/* Core Permission Warning */}
            {permission.is_core && (
              <Alert severity="warning">
                <Typography variant="caption">
                  <strong>Core Permission:</strong> This is a core permission seeded from specification files.
                  Deleting it may cause issues if it's referenced elsewhere in the system.
                </Typography>
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading || loadingUsage}>
          Cancel
        </Button>
        <Button
          onClick={handleDelete}
          color="error"
          variant="contained"
          disabled={loading || loadingUsage || !canDelete}
        >
          {loading ? 'Deleting...' : 'Delete Permission'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeletePermissionDialog;
